import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Zap,
  Cpu,
  Database,
  RefreshCw,
  Copy,
  Check,
  Search,
  Layers,
  Sparkles,
  ShieldCheck,
  Activity,
  Code,
  BookOpen,
  ShoppingBag,
  Image as ImageIcon,
  Globe,
  Radio,
  FileText,
  Clock,
  ArrowRight,
  AlertCircle
} from 'lucide-react';

export interface LunaCacheInspectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId?: string | number;
  authToken?: string | null;
  tenantApiKey?: string | null;
  channel?: string;
  onCachePurged?: () => void;
}

export const LunaCacheInspectionModal: React.FC<LunaCacheInspectionModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  authToken,
  tenantApiKey,
  channel = 'default',
  onCachePurged,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'nodes' | 'prompt' | 'tools'>('overview');
  const [cacheData, setCacheData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const [searchPrompt, setSearchPrompt] = useState('');
  const [copied, setCopied] = useState(false);
  const [purgeSuccessMsg, setPurgeSuccessMsg] = useState<string | null>(null);

  const effectiveTenantId = tenantId ? String(tenantId) : 'global';

  const getHeaders = () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;
    return headers;
  };

  const fetchCacheInspection = async () => {
    setIsLoading(true);
    try {
      const endpoint = `/api/admin/luna-flows/cache-inspection?tenantId=${encodeURIComponent(effectiveTenantId)}&channel=${encodeURIComponent(channel)}`;
      const res = await fetch(endpoint, {
        headers: getHeaders(),
      });
      const data = await res.json();
      if (data.success && data.cache) {
        setCacheData(data.cache);
      }
    } catch (err) {
      console.warn('[LunaCacheInspectionModal] Error fetching cache data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchCacheInspection();
      setPurgeSuccessMsg(null);
    }
  }, [isOpen, effectiveTenantId, channel]);

  const handlePurgeAndReindex = async () => {
    setIsPurging(true);
    setPurgeSuccessMsg(null);
    try {
      const res = await fetch('/api/admin/luna-flows/purge-cache', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          tenantId: effectiveTenantId,
          channel,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setPurgeSuccessMsg('Cache successfully purged & re-indexed across all active nodes!');
        await fetchCacheInspection();
        if (onCachePurged) onCachePurged();
      }
    } catch (err) {
      console.error('[LunaCacheInspectionModal] Purge error:', err);
    } finally {
      setIsPurging(false);
    }
  };

  const handleCopyPrompt = () => {
    const text = cacheData?.fullSystemPrompt || cacheData?.systemPromptPreview || '';
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  const totalTokens = cacheData?.estimatedTokens || 0;
  const isNative = cacheData?.isNativeCached || false;
  const nodesSummary = cacheData?.nodesSummary || [];
  const tools = cacheData?.tools || [];
  const promptText = cacheData?.fullSystemPrompt || cacheData?.systemPromptPreview || '';

  const filteredPromptLines = searchPrompt.trim()
    ? promptText.split('\n').filter((l: string) => l.toLowerCase().includes(searchPrompt.toLowerCase())).join('\n')
    : promptText;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-4xl max-h-[92vh] flex flex-col rounded-3xl bg-slate-900 border border-cyan-500/30 shadow-[0_0_50px_rgba(6,182,212,0.25)] overflow-hidden animate-in zoom-in-95 duration-200">
        
        {/* Glowing Aura Accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 via-emerald-400 to-indigo-500 animate-pulse" />

        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-cyan-950/40 via-slate-900 to-slate-900">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-cyan-500/20 border border-cyan-400/40 text-cyan-300 shadow-[0_0_15px_rgba(6,182,212,0.4)]">
              <Cpu size={22} className="animate-spin text-cyan-300 [animation-duration:12s]" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base font-black text-white uppercase tracking-wider font-sans">
                  Luna Neural Context Cache & Token Engine
                </h3>
                <span className={`text-[9px] font-mono font-black uppercase px-2 py-0.5 rounded-full border ${
                  isNative
                    ? 'bg-emerald-500/20 border-emerald-400/50 text-emerald-300 shadow-[0_0_10px_rgba(16,185,129,0.4)]'
                    : 'bg-cyan-500/20 border-cyan-400/50 text-cyan-300'
                }`}>
                  {isNative ? '⚡ Tier-2 Context Cache (90% OFF)' : '⚡ High-Speed In-Memory Cache'}
                </span>
              </div>
              <p className="text-xs text-slate-400 font-mono flex items-center gap-2 mt-0.5">
                <span>Tenant: <span className="text-cyan-300 font-bold">{effectiveTenantId}</span></span>
                <span>•</span>
                <span>Channel: <span className="text-emerald-300 font-bold">{channel}</span></span>
                <span>•</span>
                <span>Latency: <span className="text-cyan-300 font-bold">&lt; 0.05ms</span></span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePurgeAndReindex}
              disabled={isPurging}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-cyan-600/30 to-emerald-600/30 border border-cyan-500/40 hover:border-cyan-400 text-cyan-200 text-xs font-bold transition-all hover:scale-105 active:scale-95 disabled:opacity-50 cursor-pointer shadow-[0_0_15px_rgba(6,182,212,0.2)]"
              title="Flush current compiled cache and re-index all active nodes immediately"
            >
              <RefreshCw size={13} className={isPurging ? 'animate-spin text-cyan-300' : 'text-cyan-400'} />
              <span>{isPurging ? 'Re-indexing...' : 'Purge & Re-index'}</span>
            </button>

            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Success Alert Banner */}
        {purgeSuccessMsg && (
          <div className="px-6 py-2 bg-emerald-950/80 border-b border-emerald-500/40 text-emerald-300 text-xs font-mono font-bold flex items-center gap-2 animate-fadeIn">
            <Check size={14} className="text-emerald-400" />
            <span>{purgeSuccessMsg}</span>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex items-center gap-2 px-6 pt-3 border-b border-white/5 bg-slate-900/50">
          <button
            type="button"
            onClick={() => setActiveTab('overview')}
            className={`pb-2.5 px-3 text-xs font-bold tracking-wider uppercase transition-all border-b-2 ${
              activeTab === 'overview'
                ? 'border-cyan-400 text-cyan-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            ⚡ Neural Overview
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('nodes')}
            className={`pb-2.5 px-3 text-xs font-bold tracking-wider uppercase transition-all border-b-2 flex items-center gap-1.5 ${
              activeTab === 'nodes'
                ? 'border-cyan-400 text-cyan-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Layers size={13} />
            <span>Connected Nodes ({nodesSummary.length})</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('prompt')}
            className={`pb-2.5 px-3 text-xs font-bold tracking-wider uppercase transition-all border-b-2 flex items-center gap-1.5 ${
              activeTab === 'prompt'
                ? 'border-cyan-400 text-cyan-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <FileText size={13} />
            <span>Compiled Prompt ({Math.round(totalTokens).toLocaleString()} Tokens)</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('tools')}
            className={`pb-2.5 px-3 text-xs font-bold tracking-wider uppercase transition-all border-b-2 flex items-center gap-1.5 ${
              activeTab === 'tools'
                ? 'border-cyan-400 text-cyan-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Code size={13} />
            <span>Registered Tools ({tools.length})</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400 space-y-3">
              <RefreshCw size={28} className="animate-spin text-cyan-400" />
              <p className="text-xs font-mono">Reading neural cache telemetry from token server...</p>
            </div>
          ) : (
            <>
              {/* TAB 1: OVERVIEW */}
              {activeTab === 'overview' && (
                <div className="space-y-4 animate-fadeIn">
                  {/* Executive Metric Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="p-4 rounded-2xl bg-slate-800/60 border border-white/5 space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1">
                        <Zap size={11} className="text-cyan-400" /> Compiled Tokens
                      </span>
                      <p className="text-xl font-black text-white font-mono">
                        {totalTokens.toLocaleString()}
                      </p>
                      <p className="text-[10px] text-cyan-400 font-mono">
                        {isNative ? '⚡ Tier-2 Active (90% OFF)' : '⚡ In-Memory (<0.05ms)'}
                      </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-800/60 border border-white/5 space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1">
                        <Activity size={11} className="text-emerald-400" /> Read Latency
                      </span>
                      <p className="text-xl font-black text-emerald-400 font-mono">
                        &lt; 0.05 ms
                      </p>
                      <p className="text-[10px] text-slate-400 font-mono">
                        Zero DB queries on customer messages
                      </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-800/60 border border-white/5 space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1">
                        <Layers size={11} className="text-indigo-400" /> Active Nodes
                      </span>
                      <p className="text-xl font-black text-indigo-300 font-mono">
                        {nodesSummary.length}
                      </p>
                      <p className="text-[10px] text-slate-400 font-mono">
                        Traversed & pre-digested
                      </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-800/60 border border-white/5 space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1">
                        <Code size={11} className="text-purple-400" /> Action Tools
                      </span>
                      <p className="text-xl font-black text-purple-300 font-mono">
                        {tools.length}
                      </p>
                      <p className="text-[10px] text-slate-400 font-mono">
                        Executable function declarations
                      </p>
                    </div>
                  </div>

                  {/* Cache State & Cost Optimization Card */}
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-cyan-950/40 via-slate-800/60 to-slate-900 border border-cyan-500/20 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-black uppercase tracking-wider text-cyan-300 flex items-center gap-1.5">
                        <ShieldCheck size={14} className="text-cyan-400" /> Token Server Caching Intelligence
                      </span>
                      <span className="text-[10px] font-mono text-slate-400">
                        TTL: {cacheData?.ttlRemainingSeconds ? `${Math.round(cacheData.ttlRemainingSeconds / 60)}m remaining` : 'Persistent'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      <div className="p-3 rounded-xl bg-slate-900/80 border border-white/5 space-y-1">
                        <p className="text-[11px] font-bold text-white flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full bg-cyan-400 animate-ping inline-block" />
                          Tier 1: Kangavena In-Memory Graph Cache
                        </p>
                        <p className="text-[11px] text-slate-300 leading-relaxed">
                          The active flow graph, node data, and tool definitions are compiled once and served from persistent memory. Customer greetings ("Hi", "Kiyada") execute with <b>zero database lag</b>.
                        </p>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-900/80 border border-white/5 space-y-1">
                        <p className="text-[11px] font-bold text-emerald-300 flex items-center gap-1">
                          <span className={`w-2 h-2 rounded-full ${isNative ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'} inline-block`} />
                          Tier 2: Native Context Cache (90% Cost Reduction)
                        </p>
                        <p className="text-[11px] text-slate-300 leading-relaxed">
                          {isNative
                            ? `Active! Flow tokens (${totalTokens.toLocaleString()}) meet the 32,768 threshold. All repeat prompt reads receive the 90% discount ($0.075/1M tokens).`
                            : `Standby. Automatically unlocks 90% token cost reduction when flow tokens reach 32,768 (current: ${totalTokens.toLocaleString()}).`}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Connected Nodes Quick Summary */}
                  <div className="space-y-2">
                    <p className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                      Pre-Compiled Knowledge & Sensory Feeds
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {nodesSummary.map((node: any, idx: number) => (
                        <div key={idx} className="p-3 rounded-xl bg-slate-800/40 border border-white/5 flex items-center justify-between">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center text-cyan-300 shrink-0">
                              {node.type === 'ProductCatalogNode' ? <ShoppingBag size={13} /> :
                               node.type === 'KnowledgeNode' ? <BookOpen size={13} /> :
                               node.type === 'MediaVaultNode' ? <ImageIcon size={13} /> :
                               node.type === 'ScraperNode' ? <Globe size={13} /> :
                               node.type === 'WhatsAppInstanceNode' ? <Radio size={13} /> :
                               <Sparkles size={13} />}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-bold text-white truncate">{node.title}</p>
                              <p className="text-[10px] text-slate-400 truncate">{node.preview}</p>
                            </div>
                          </div>
                          {node.badge && (
                            <span className="text-[9px] font-mono font-bold px-2 py-0.5 rounded bg-slate-700/60 text-cyan-300 border border-white/5 shrink-0">
                              {node.badge}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: CONNECTED NODES */}
              {activeTab === 'nodes' && (
                <div className="space-y-3 animate-fadeIn">
                  <p className="text-xs text-slate-400">
                    The following nodes are currently connected and active on the flow canvas. Their data is merged into Luna's central intelligence:
                  </p>
                  <div className="space-y-2">
                    {nodesSummary.map((node: any, idx: number) => (
                      <div key={idx} className="p-3.5 rounded-2xl bg-slate-800/50 border border-white/10 flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-xs font-bold text-white">{node.title}</h4>
                            <span className="text-[9px] font-mono text-cyan-400 bg-cyan-950/60 px-2 py-0.5 rounded border border-cyan-500/30">
                              {node.type}
                            </span>
                          </div>
                          <p className="text-xs text-slate-300 font-sans">{node.preview}</p>
                        </div>
                        {node.badge && (
                          <span className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shrink-0">
                            {node.badge}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TAB 3: COMPILED SYSTEM PROMPT */}
              {activeTab === 'prompt' && (
                <div className="space-y-3 animate-fadeIn">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[200px]">
                      <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        value={searchPrompt}
                        onChange={(e) => setSearchPrompt(e.target.value)}
                        placeholder="Search compiled instructions, prices, rules..."
                        className="w-full bg-slate-950/80 border border-white/10 rounded-xl pl-8 pr-3 py-1.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-400 font-mono"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleCopyPrompt}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800 border border-white/10 hover:border-cyan-400 text-xs font-bold text-slate-200 hover:text-white transition cursor-pointer"
                    >
                      {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} className="text-cyan-400" />}
                      <span>{copied ? 'Copied to Clipboard!' : 'Copy Full Prompt'}</span>
                    </button>
                  </div>

                  <div className="p-4 rounded-2xl bg-slate-950 border border-white/10 font-mono text-[11px] text-slate-300 leading-relaxed max-h-[50vh] overflow-y-auto whitespace-pre-wrap select-text">
                    {filteredPromptLines || 'No compiled instructions found.'}
                  </div>
                </div>
              )}

              {/* TAB 4: REGISTERED TOOLS */}
              {activeTab === 'tools' && (
                <div className="space-y-3 animate-fadeIn">
                  <p className="text-xs text-slate-400">
                    Downstream action nodes compile into executable Luna Action Tool declarations. Luna autonomously invokes these when required:
                  </p>
                  {tools.length === 0 ? (
                    <div className="p-8 text-center text-slate-500 text-xs font-mono">
                      No downstream function tools registered in this flow.
                    </div>
                  ) : (
                    <div className="space-y-2.5">
                      {tools.map((t: any, idx: number) => (
                        <div key={idx} className="p-3.5 rounded-2xl bg-slate-800/50 border border-white/10 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-mono font-bold text-cyan-300 flex items-center gap-1.5">
                              <Code size={13} className="text-purple-400" /> {t.name}
                            </span>
                            <span className="text-[9px] font-mono text-slate-400 bg-slate-900 px-2 py-0.5 rounded border border-white/5">
                              DYNAMIC FUNCTION
                            </span>
                          </div>
                          <p className="text-xs text-slate-300">{t.description}</p>
                          {t.parameters && (
                            <details className="text-[10px] text-slate-400 pt-1 font-mono cursor-pointer">
                              <summary className="text-cyan-400 hover:underline">View Parameter Schema</summary>
                              <pre className="mt-1 p-2 rounded bg-slate-950/80 text-slate-300 overflow-x-auto text-[9px]">
                                {JSON.stringify(t.parameters, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3 border-t border-white/10 bg-slate-950 flex items-center justify-between">
          <span className="text-[10px] font-mono text-slate-500">
            Kangavena Neural Cache Server • Sub-millisecond Execution Standard
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
};

export default LunaCacheInspectionModal;
